import { useMemo } from 'react';
import { GONDOLAS, etiquetaCat } from '../../../domain/pg/catalogo';
import {
  ojosPg, participacionDeGondola, participacionPg, precioModerno,
} from '../../../domain/pg/gondola';
import {
  abastecimiento, categoriasDeclaradas, cobertura, fueraDeLista, marcasMedidas,
  preciosPorMarca, preciosSinPresencia, presenciaPorMarca, presenciasHuerfanas,
  puestosRepetidos, recomendaciones, tipoDeCliente, type Conteo,
} from '../../../domain/pg/mercados';
import type { CierreMercado, FichaMercado, FichaModerno } from '../../../domain/pg/tipos';
import { fueraDeLaMaestra, nombreDeTienda } from '../../../domain/pg/tipos';
import type { GrupoMercado } from '../../../domain/pg/normalizar';
import TarjetasResumen, { type Indicador } from '../../../components/TarjetasResumen';
import { Seccion, Senal } from '../../../components/DetalleExpandible';
import type { Instrumento } from '../LevantamientoPgBandeja';

/** Una barra. Sin librería de gráficos: son `div`s de ancho porcentual y no justifican una. */
function Barra({ pct, color = 'bg-blue-600' }: { pct: number; color?: string }) {
  return (
    <span className="inline-block h-2 w-40 rounded bg-gray-100 align-middle">
      <span className={`block h-2 rounded ${color}`} style={{ width: `${Math.max(1, Math.min(100, pct))}%` }} />
    </span>
  );
}

function ListaConteo({ datos, de, nota }: { datos: readonly Conteo[]; de: number; nota?: string }) {
  if (datos.length === 0) return <p className="text-sm text-gray-500">Sin respuestas.</p>;
  return (
    <>
      <ul className="space-y-1">
        {datos.map((d) => (
          <li key={d.valor} className="flex items-center gap-2 text-sm">
            <span className="w-64 shrink-0">{d.valor}</span>
            <Barra pct={de === 0 ? 0 : (d.n / de) * 100} />
            <b className="tabular-nums">{d.n}</b>
            <span className="text-xs text-gray-500">
              {de === 0 ? '' : `${Math.round((d.n / de) * 100)} %`}
            </span>
          </li>
        ))}
      </ul>
      {nota !== undefined && <p className="mt-2 text-xs text-gray-500">{nota}</p>}
    </>
  );
}

/**
 * El consolidado, calculado sobre **las mismas filas que muestra «Registros»**.
 *
 * 🔴 **Cada indicador lleva su denominador escrito al lado, adentro del rótulo.** No es prolijidad:
 * los denominadores de esta pantalla no son todos el mismo número y confundirlos produce cifras
 * plausibles y falsas.
 *
 * - La cobertura va sobre los puestos **visitados**, no sobre los levantados. El propio formulario
 *   lo dice en la pregunta: *«un recorrido de treinta puestos con ocho medidos se lee como un
 *   recorrido de ocho»*.
 * - La presencia de cada marca va sobre las fichas **que declararon esa categoría**, y ese número
 *   cambia marca por marca: a un puesto que solo vende shampoo nunca se le preguntó por Ariel.
 * - El share de recomendación va sobre las que **contestaron**, y `No contestó` es una opción de la
 *   lista, o sea un valor guardado que hay que sacar a mano o queda cuarto en el ranking.
 */
export default function ResumenPg({
  instrumento, mercados, moderno, cierres, gruposMercado,
}: {
  instrumento: Instrumento;
  mercados: readonly FichaMercado[];
  moderno: readonly FichaModerno[];
  cierres: readonly CierreMercado[];
  gruposMercado: readonly GrupoMercado[];
}) {
  if (instrumento === 'mercados') {
    return <ResumenMercados fichas={mercados} cierres={cierres} grupos={gruposMercado} />;
  }
  return <ResumenModerno fichas={moderno} />;
}

// ═══════════════════════════════════════════════════════════════════════════

function ResumenMercados({ fichas, cierres, grupos }: {
  fichas: readonly FichaMercado[];
  cierres: readonly CierreMercado[];
  grupos: readonly GrupoMercado[];
}) {
  const cob = useMemo(() => cobertura(fichas, cierres), [fichas, cierres]);
  const presencia = useMemo(() => presenciaPorMarca(fichas), [fichas]);
  const precios = useMemo(() => preciosPorMarca(fichas), [fichas]);
  const huerfanos = useMemo(() => preciosSinPresencia(fichas), [fichas]);
  const rec = useMemo(() => recomendaciones(fichas), [fichas]);
  const abasto = useMemo(() => abastecimiento(fichas), [fichas]);
  const clientes = useMemo(() => tipoDeCliente(fichas), [fichas]);
  const cats = useMemo(() => categoriasDeclaradas(fichas), [fichas]);
  const fuera = useMemo(() => fueraDeLista(fichas), [fichas]);
  const repetidos = useMemo(() => puestosRepetidos(fichas), [fichas]);
  const huerfanasPres = useMemo(() => presenciasHuerfanas(fichas), [fichas]);

  if (fichas.length === 0) {
    return <p className="text-sm text-gray-600">No hay fichas en este rango para resumir.</p>;
  }

  const sinNormalizar = grupos.filter((g) => g.sinNormalizar).reduce((s, g) => s + g.n, 0);
  const indicadores: Indicador[] = [
    { titulo: 'Puestos visitados', valor: cob.visitados, detalle: 'la base de todo lo de abajo' },
    { titulo: 'Levantados', valor: cob.levantados, detalle: `de ${cob.visitados} visitados`, tono: 'bueno' },
    { titulo: 'No se pudo', valor: cob.noSePudo, detalle: `de ${cob.visitados} visitados`, tono: 'atencion' },
    { titulo: 'Mercados', valor: grupos.length, detalle: `de ${new Set(fichas.map((f) => f.mercadoNombre)).size} grafías escritas` },
    { titulo: 'Marcas medidas', valor: marcasMedidas(fichas), detalle: 'con alguna respuesta' },
    { titulo: 'Jornadas cerradas', valor: cob.jornadasCerradas, detalle: `de ${cob.mercadoDias} mercado-día con fichas`, tono: cob.jornadasCerradas < cob.mercadoDias ? 'atencion' : 'bueno' },
  ];

  const pgArriba = presencia.filter((p) => p.esPg);
  const competencia = presencia.filter((p) => !p.esPg);
  const quiebres = [...presencia].filter((p) => p.quiebre > 0)
    .sort((a, b) => b.quiebre - a.quiebre);

  const preciosPorCat = [...new Set(precios.map((p) => p.catId))]
    .map((id) => ({ id, nom: etiquetaCat(id), filas: precios.filter((p) => p.catId === id) }));

  return (
    <div className="space-y-6">
      <TarjetasResumen indicadores={indicadores} />

      <Seccion titulo="Cobertura del recorrido" ancho>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-gray-500">Por qué no se pudo levantar</dt>
            <dd className="mt-1">
              <ListaConteo datos={cob.motivos} de={cob.noSePudo} />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">
              Categorías declaradas · de {cob.levantados} puestos levantados
            </dt>
            <dd className="mt-1"><ListaConteo datos={cats} de={cob.levantados} /></dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Otros</dt>
            <dd className="mt-1 space-y-1 text-sm">
              <p>Con foto del puesto: <b>{cob.conFoto}</b> de {cob.visitados}</p>
              <p>
                Mercado escrito a mano: <b>{cob.escritosAMano}</b> de {cob.visitados}
                <span className="block text-xs text-gray-500">
                  La lista cerrada del formulario solo trae los 10 mercados de Lima.
                </span>
              </p>
              {sinNormalizar > 0 && (
                <p>
                  Fichas en un mercado sin rótulo canónico: <b>{sinNormalizar}</b>
                  <span className="block text-xs text-gray-500">
                    Se muestran con el nombre que se escribió y marcadas «sin normalizar». Se
                    arregla agregando una fila a la tabla de rótulos, no adivinando.
                  </span>
                </p>
              )}
            </dd>
          </div>
        </dl>
      </Seccion>

      <Seccion titulo="Presencia · marcas de P&G" ancho>
        <TablaPresencia filas={pgArriba} />
      </Seccion>

      <Seccion titulo="Presencia · competencia" ancho>
        <TablaPresencia filas={competencia} />
      </Seccion>

      {quiebres.length > 0 && (
        <Seccion titulo="Quiebres · la venta perdida de esta semana" ancho>
          <p className="mb-2 text-xs text-gray-600">
            «Se le acabó» es una marca que el puesto trabaja y hoy no tiene: se arregla con una
            llamada al distribuidor. Va aparte de «no la trabaja», que es otra conversación.
          </p>
          <ul className="space-y-1">
            {quiebres.slice(0, 15).map((p) => (
              <li key={`${p.cat.id}-${p.marca}`} className="flex items-center gap-2 text-sm">
                <span className="w-56 shrink-0">
                  {p.marca} <span className="text-xs text-gray-500">{p.cat.nom}</span>
                </span>
                <Barra pct={(p.quiebre / p.de) * 100} color="bg-amber-500" />
                <b className="tabular-nums">{p.quiebre}</b>
                <span className="text-xs text-gray-500">de {p.de} puestos que venden la categoría</span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      <Seccion titulo="Precios" ancho>
        <p className="mb-2 text-xs text-gray-600">
          Se muestran <b>dos</b> números y no uno. El precio tal cual se levantó es lo único que se
          midió sin interpretar. El precio por unidad solo se calcula sobre las filas que declararon
          más de una unidad: de las demás no se sabe si el número es el del paquete o el de la
          unidad, y mezclarlas destruye la mediana.
        </p>
        <div className="space-y-4">
          {preciosPorCat.map((c) => (
            <div key={c.id}>
              <h5 className="mb-1 text-xs font-semibold text-gray-700">{c.nom}</h5>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">Marca</th>
                      <th className="py-1 pr-3 text-right">Precios</th>
                      <th className="py-1 pr-3 text-right">Mediana S/</th>
                      <th className="py-1 pr-3 text-right">Rango S/</th>
                      <th className="py-1 pr-3 text-right">Por unidad</th>
                      <th className="py-1 text-right">Sobre</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {c.filas.sort((a, b) => b.n - a.n).map((p) => (
                      <tr key={p.marca}>
                        <td className="py-1 pr-3">
                          {p.esMarca ? p.marca : <span className="text-gray-500">{p.marca}</span>}
                          {p.esPg && <span className="ml-1 text-xs text-blue-700">P&amp;G</span>}
                        </td>
                        <td className="py-1 pr-3 text-right tabular-nums">{p.n}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">
                          {p.medianaPrecio === null ? '—' : p.medianaPrecio.toFixed(2)}
                        </td>
                        <td className="py-1 pr-3 text-right tabular-nums text-gray-600">
                          {p.minPrecio === null ? '—' : `${p.minPrecio.toFixed(2)} – ${(p.maxPrecio ?? 0).toFixed(2)}`}
                        </td>
                        <td className="py-1 pr-3 text-right tabular-nums">
                          {p.medianaPorUnidad === null ? '—' : p.medianaPorUnidad.toFixed(2)}
                        </td>
                        <td className="py-1 text-right text-xs text-gray-500">
                          {p.conUnidades} de {p.n}
                          {p.comoPaquete > 0 && (
                            <span className="block">{p.comoPaquete} como paquete</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </Seccion>

      <Seccion titulo="Qué recomienda el puestero cuando no le piden marca" ancho>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs text-gray-500">
              Marcas · de {rec.de} puestos que contestaron
            </p>
            <ListaConteo datos={rec.marcas} de={rec.de} />
            {rec.otrasRespuestas.length > 0 && (
              <>
                <p className="mb-1 mt-3 text-xs text-gray-500">
                  Respuestas que no son una marca
                </p>
                <ListaConteo datos={rec.otrasRespuestas} de={rec.de} />
              </>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Contestaron literalmente «No contestó»: <b>{rec.noContesto}</b>. Quedan fuera del
              numerador <b>y</b> del denominador: una no-respuesta no compite con las marcas.
              Sin ninguna respuesta: {rec.sinDato}.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              ⚠️ La lista de marcas que se le ofreció a cada puestero depende de lo que declaró
              vender, así que el denominador no es el mismo en todos. El orden se puede leer;
              comparar dos marcas de categorías distintas, no.
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">¿Por qué esa?</p>
            <ListaConteo datos={rec.motivos} de={rec.motivos.reduce((s, m) => s + m.n, 0)} />
          </div>
        </div>
      </Seccion>

      <Seccion titulo="De dónde saca el producto" ancho>
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <p className="mb-1 text-xs text-gray-500">
              ¿De dónde saca el producto? · de {abasto.de} puestos que contestaron
            </p>
            <ListaConteo datos={abasto.donde} de={abasto.de}
              nota="Se puede marcar más de uno, así que la suma pasa del total de puestos." />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">¿Cada cuánto le llega?</p>
            <ListaConteo datos={abasto.frecuencia}
              de={abasto.frecuencia.reduce((s, x) => s + x.n, 0)} />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">¿Lo visita algún vendedor o impulsador?</p>
            <ListaConteo datos={abasto.visita} de={abasto.visita.reduce((s, x) => s + x.n, 0)} />
          </div>
        </div>
      </Seccion>

      <Seccion titulo="A quién le vende este puesto" ancho>
        <p className="mb-1 text-xs text-gray-500">
          De {clientes.de} puestos que contestaron. Se puede marcar más de uno.
        </p>
        <ListaConteo datos={clientes.conteo} de={clientes.de} />
        <p className="mt-2 text-xs text-gray-600">
          Un puesto que le vende a bodegueros <b>es un canal, no un punto de venta</b>: lo que pase
          ahí se multiplica por todas las bodegas que se abastecen de él.
        </p>
      </Seccion>

      {fuera.length > 0 && (
        <Seccion titulo="Marcas que el campo vio y el catálogo no tenía" ancho>
          <p className="mb-2 text-xs text-gray-600">
            Texto crudo, sin normalizar y sin contar: es la puerta por donde entran las marcas
            nuevas al estudio, no una métrica.
          </p>
          <ul className="list-inside list-disc space-y-0.5 text-sm">
            {fuera.map((x) => (
              <li key={x.valor}>{x.valor}{x.n > 1 && <span className="text-gray-500"> ×{x.n}</span>}</li>
            ))}
          </ul>
        </Seccion>
      )}

      <Seccion titulo="Lo que conviene revisar" ancho>
        <ul className="space-y-2 text-sm">
          {huerfanos.length > 0 && (
            <li>
              <Senal tono="grave">{huerfanos.length}</Senal>{' '}
              precios de una marca que la misma ficha <b>no marcó como presente</b>. Entran igual al
              total de su categoría —el precio se levantó de verdad— pero conviene mirarlos: el
              formulario deja cargar un precio a mano de cualquier marca de la categoría, presente o
              no.
            </li>
          )}
          {huerfanasPres > 0 && (
            <li>
              <Senal>{huerfanasPres}</Senal>{' '}
              respuestas de presencia de categorías que la ficha terminó <b>no declarando</b>. El
              formulario avisa y deja enviar. Quedan fuera del denominador de su marca y se cuentan
              acá para que la suma cierre.
            </li>
          )}
          {repetidos.length > 0 && (
            <li>
              <Senal>{repetidos.length}</Senal>{' '}
              números de puesto repetidos en el mismo mercado, día y supervisor. El identificador de
              cliente evita el duplicado por reintento, no el duplicado por recorrido.
            </li>
          )}
          {grupos.some((g) => g.rotuloNoCoincideConGps) && (
            <li>
              <Senal tono="grave">
                {grupos.filter((g) => g.rotuloNoCoincideConGps).reduce((s, g) => s + g.n, 0)}
              </Senal>{' '}
              fichas eligieron un mercado de la lista cerrada y su GPS las pone en otra ciudad.
              Casi seguro es alguien que eligió el primer nombre disponible porque su mercado no
              estaba en la lista.
            </li>
          )}
          {cob.jornadasCerradas < cob.mercadoDias && (
            <li>
              <Senal>{cob.mercadoDias - cob.jornadasCerradas}</Senal>{' '}
              mercado-día con fichas y sin cierre de jornada.
            </li>
          )}
          {huerfanos.length === 0 && huerfanasPres === 0 && repetidos.length === 0
            && cob.jornadasCerradas >= cob.mercadoDias
            && !grupos.some((g) => g.rotuloNoCoincideConGps) && (
            <li className="text-gray-600">Nada que revisar en este rango.</li>
          )}
        </ul>
      </Seccion>
    </div>
  );
}

function TablaPresencia({ filas }: { filas: readonly ReturnType<typeof presenciaPorMarca>[number][] }) {
  if (filas.length === 0) return <p className="text-sm text-gray-500">Sin mediciones.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="py-1 pr-3">Marca</th>
            <th className="py-1 pr-3">Categoría</th>
            <th className="py-1 pr-3">Tiene</th>
            <th className="py-1 pr-3 text-right">Se le acabó</th>
            <th className="py-1 pr-3 text-right">No la trabaja</th>
            <th className="py-1 pr-3 text-right">Sin responder</th>
            <th className="py-1 text-right">Denominador</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[...filas].sort((a, b) => b.pctPresente - a.pctPresente).map((p) => (
            <tr key={`${p.cat.id}-${p.marca}`}>
              <td className="py-1 pr-3">
                {p.marca}
                {p.ambigua && (
                  <span
                    className="ml-1 cursor-help text-xs text-amber-700"
                    title="Esta marca está en dos categorías y el formulario guarda la respuesta una sola vez: la cifra que ves acá alimenta las dos filas."
                  >
                    ⚠
                  </span>
                )}
              </td>
              <td className="py-1 pr-3 text-xs text-gray-500">{p.cat.nom}</td>
              <td className="py-1 pr-3">
                <span className="inline-flex items-center gap-2">
                  <Barra pct={p.pctPresente} color="bg-green-600" />
                  <b className="tabular-nums">{p.pctPresente} %</b>
                  <span className="text-xs text-gray-500">({p.presente})</span>
                </span>
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">
                {p.quiebre > 0 ? <b className="text-amber-700">{p.quiebre}</b> : p.quiebre}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums text-gray-600">{p.noTrabaja}</td>
              <td className="py-1 pr-3 text-right tabular-nums text-gray-400">{p.sinRespuesta}</td>
              <td className="py-1 text-right text-xs text-gray-500">
                de {p.de} puestos que venden {p.cat.nom.toLowerCase()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

function ResumenModerno({ fichas }: { fichas: readonly FichaModerno[] }) {
  const porGondola = useMemo(() => GONDOLAS.map((g) => {
    const conEspacio = fichas
      .map((f) => ({ f, esp: f.ficha.gondolas?.[g.id]?.espacio }))
      .map((x) => ({ ...x, p: participacionPg(g.id, x.esp) }))
      .filter((x) => x.p.medida);

    const promedio = conEspacio.length === 0 ? null
      : Math.round(conEspacio.reduce((s, x) => s + x.p.pct, 0) / conEspacio.length);

    const ojos = fichas
      .map((f) => ojosPg(g.id, f.ficha.gondolas?.[g.id]?.espacio))
      .filter((o) => o.aplica && o.medida);
    const ojosPct = ojos.length === 0 ? null
      : Math.round(ojos.reduce((s, o) => s + o.pct, 0) / ojos.length);
    const sinNivelDeOjos = fichas
      .filter((f) => !ojosPg(g.id, f.ficha.gondolas?.[g.id]?.espacio).aplica).length;

    // Por marca: el promedio de los porcentajes POR FICHA, no la suma de celdas de todas las
    // tiendas. Sumando celdas, una tienda con la góndola más ancha valdría por tres y el número
    // dejaría de responder «cuánto le toca a cada marca en una tienda típica».
    const marcas = g.marcas.map((marca) => {
      const pcts = fichas
        .map((f) => participacionDeGondola(f.ficha.gondolas?.[g.id]?.espacio))
        .filter((p) => p.medida)
        .map((p) => p.marcas.find((m) => m.marca === marca)?.pct ?? 0);
      return {
        marca,
        pct: pcts.length === 0 ? 0 : Math.round(pcts.reduce((s, x) => s + x, 0) / pcts.length),
      };
    });

    return { g, medidas: conEspacio.length, promedio, ojosPct, ojosSobre: ojos.length, sinNivelDeOjos, marcas };
  }), [fichas]);

  const precios = useMemo(() => {
    const todos = fichas.flatMap((f) => GONDOLAS.flatMap(
      (g) => (f.ficha.gondolas?.[g.id]?.precios ?? []).map((p) => ({ ...p, gondola: g.nom })),
    ));
    return {
      total: todos.length,
      choques: todos.filter((p) => p.tipo === 'choque').length,
      condiciones: conteo(todos.map((p) => (p.condicion ?? '').trim()).filter((x) => x !== '')),
      motivos: conteo(todos.filter((p) => p.tipo === 'choque')
        .map((p) => (p.motivo ?? '').trim()).filter((x) => x !== '')),
      // Los precios son texto libre sin validar. Cuántos no se pueden leer como número es
      // información de calidad del dato, no un error a esconder.
      ilegibles: todos.filter((p) => (p.normal ?? '').trim() !== '' && precioModerno(p.normal) === null).length,
      conFichas: fichas.filter((f) => GONDOLAS.some(
        (g) => (f.ficha.gondolas?.[g.id]?.precios ?? []).length > 0,
      )).length,
    };
  }, [fichas]);

  const promos = useMemo(() => {
    const todas = fichas.flatMap((f) => GONDOLAS.flatMap(
      (g) => f.ficha.gondolas?.[g.id]?.promos ?? [],
    ));
    return {
      total: todas.length,
      mecanicas: conteo(todas.map((p) => (p.mecanica ?? '').trim()).filter((x) => x !== '')),
      tarjetas: conteo(todas.map((p) => (p.tarjeta ?? '').trim()).filter((x) => x !== '')),
      conFichas: fichas.filter((f) => GONDOLAS.some(
        (g) => (f.ficha.gondolas?.[g.id]?.promos ?? []).length > 0,
      )).length,
    };
  }, [fichas]);

  const pop = useMemo(() => {
    const todos = fichas.flatMap((f) => GONDOLAS.flatMap((g) => f.ficha.gondolas?.[g.id]?.pop ?? []));
    return {
      total: todos.length,
      tipos: conteo(todos.map((x) => (x.tipo ?? '').trim()).filter((x) => x !== '')),
      marcas: conteo(todos.map((x) => (x.marca ?? '').trim()).filter((x) => x !== '')),
    };
  }, [fichas]);

  const nuevos = useMemo(() => fichas.flatMap((f) => GONDOLAS.flatMap((g) => {
    const gd = f.ficha.gondolas?.[g.id];
    return [
      { t: (gd?.otros_pg ?? '').trim(), de: `${g.nom} · P&G` },
      { t: (gd?.otros_comp ?? '').trim(), de: `${g.nom} · competencia` },
    ].filter((x) => x.t !== '').map((x) => ({ ...x, tienda: nombreDeTienda(f) }));
  })), [fichas]);

  if (fichas.length === 0) {
    return <p className="text-sm text-gray-600">No hay fichas en este rango para resumir.</p>;
  }

  const completo = conteo(fichas.map((f) => (f.ficha.cierre?.completo ?? '').trim())
    .filter((x) => x !== ''));
  const aMano = fichas.filter(fueraDeLaMaestra).length;

  const indicadores: Indicador[] = [
    { titulo: 'Tiendas visitadas', valor: fichas.length, detalle: `${new Set(fichas.map(nombreDeTienda)).size} tiendas distintas` },
    ...porGondola.map((x): Indicador => ({
      titulo: `P&G en ${x.g.nom.toLowerCase()}`,
      valor: x.promedio ?? 0,
      detalle: x.promedio === null
        ? 'ninguna góndola medida'
        : `% promedio · de ${x.medidas} de ${fichas.length} con espacio medido`,
      tono: 'neutro',
    })),
    { titulo: 'Precios levantados', valor: precios.total, detalle: `${precios.choques} son choques · en ${precios.conFichas} tiendas` },
    { titulo: 'Material publicitario', valor: pop.total, detalle: `de ${fichas.length} tiendas visitadas` },
    { titulo: 'Tienda escrita a mano', valor: aMano, detalle: `de ${fichas.length} · no está en la maestra`, tono: 'atencion' },
  ];

  return (
    <div className="space-y-6">
      <TarjetasResumen indicadores={indicadores} />

      {porGondola.map((x) => (
        <Seccion key={x.g.id} titulo={`Góndola de ${x.g.nom.toLowerCase()}`} ancho>
          {x.medidas === 0 ? (
            <p className="text-sm text-gray-600">Ninguna ficha de este rango midió esta góndola.</p>
          ) : (
            <>
              <p className="mb-2 text-xs text-gray-500">
                Participación promedio por tienda · sobre {x.medidas} de {fichas.length} fichas con
                el espacio medido. Se promedian los porcentajes de cada tienda y no se suman las
                celdas: sumando, la tienda con la góndola más ancha valdría por tres.
              </p>
              <ul className="space-y-1">
                {x.marcas.map((m) => (
                  <li key={m.marca} className="flex items-center gap-2 text-sm">
                    <span className="w-40 shrink-0">
                      {m.marca}
                      {x.g.pg.includes(m.marca) && <span className="ml-1 text-xs text-blue-700">P&amp;G</span>}
                    </span>
                    <Barra pct={m.pct} color={x.g.pg.includes(m.marca) ? 'bg-blue-600' : 'bg-gray-400'} />
                    <b className="tabular-nums">{m.pct} %</b>
                  </li>
                ))}
              </ul>
              <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-gray-500">
                    A la altura de los ojos · sobre {x.ojosSobre} fichas con nivel de ojos
                  </dt>
                  <dd className="text-sm">
                    {x.ojosPct === null ? '—' : <><b>{x.ojosPct} %</b> de P&amp;G</>}
                    {x.sinNivelDeOjos > 0 && (
                      <span className="block text-xs text-gray-500">
                        {x.sinNivelDeOjos} fichas quedan fuera: su mueble tiene 3 niveles y no
                        tiene nivel de ojos.
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </>
          )}
        </Seccion>
      ))}

      <Seccion titulo="Cobertura de la visita" ancho>
        <p className="mb-1 text-xs text-gray-500">
          ¿Pudiste medir las dos góndolas? · de {fichas.length} fichas
        </p>
        <ListaConteo datos={completo} de={fichas.length} />
        <p className="mt-2 text-xs text-gray-600">
          {completo.reduce((s, c) => s + c.n, 0)} de {fichas.length} fichas contestaron esta
          pregunta. Mientras sean pocas, el dato que de verdad distingue «la góndola no se midió» de
          «no tenía producto» son las señales <b>sin espacio</b> de cada ficha, no este indicador.
        </p>
      </Seccion>

      <Seccion titulo="Precios y choques" ancho>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs text-gray-500">
              ¿Bajo qué condición aplica el más bajo? · de {precios.total} precios
            </p>
            <ListaConteo datos={precios.condiciones} de={precios.total} />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">
              ¿Por qué lo levantas? · de {precios.choques} choques
            </p>
            <ListaConteo datos={precios.motivos} de={precios.choques} />
          </div>
        </div>
        {precios.ilegibles > 0 && (
          <p className="mt-2 text-xs text-amber-800">
            {precios.ilegibles} precios están escritos de una forma que no se puede leer como
            número. El campo del formulario es texto libre sin validar; se muestran tal cual en el
            detalle y no entran a ningún cálculo.
          </p>
        )}
      </Seccion>

      <Seccion titulo="Promociones" ancho>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs text-gray-500">
              ¿Qué dice el cartel? · de {promos.total} promociones en {promos.conFichas} tiendas
            </p>
            <ListaConteo datos={promos.mecanicas} de={promos.total} />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">¿Exige tarjeta de la cadena?</p>
            <ListaConteo datos={promos.tarjetas} de={promos.total} />
          </div>
        </div>
      </Seccion>

      <Seccion titulo="Material publicitario" ancho>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs text-gray-500">
              Qué es · de {fichas.length} tiendas visitadas
            </p>
            <ListaConteo datos={pop.tipos} de={Math.max(1, pop.total)}
              nota="Cero POP es un hallazgo, no un hueco: por eso el denominador son las tiendas visitadas y no las que tenían material." />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-500">De qué marca</p>
            <ListaConteo datos={pop.marcas} de={Math.max(1, pop.total)} />
          </div>
        </div>
      </Seccion>

      {nuevos.length > 0 && (
        <Seccion titulo="Lo que el campo vio y el catálogo no tenía" ancho>
          <p className="mb-2 text-xs text-gray-600">
            Texto crudo. Es lo que hace que la lista cerrada del año que viene sea distinta.
          </p>
          <ul className="space-y-0.5 text-sm">
            {nuevos.map((x, i) => (
              <li key={i}>
                <b>{x.t}</b>{' '}
                <span className="text-xs text-gray-500">{x.de} · {x.tienda}</span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </div>
  );
}

function conteo(xs: readonly string[]): Conteo[] {
  const m = new Map<string, number>();
  for (const x of xs) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m.entries()].map(([valor, n]) => ({ valor, n }))
    .sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es'));
}
